/** Card 4 — sample appointment preparation experience (fictional family). */
import React, { useEffect } from "react";
import { CalendarCheck, Pill, FileText, History } from "lucide-react";
import { TransitionPanel, DemoBadge, trackCompleted } from "../activationShared";

const SECTION = "rounded-2xl border border-[#E2E8F0] bg-white p-4";
const LABEL = "flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-teal-dark";

export default function VisitPrepDemo({ onClose }) {
    useEffect(() => { trackCompleted("visit_prep"); }, []);
    return (
        <div data-testid="activation-visit-prep">
            <DemoBadge>Sample visit prep — a fictional family, so you can see the format</DemoBadge>
            <h3 className="mt-4 font-heading font-medium text-lg md:text-xl text-[#1F2624]">
                Margaret&apos;s neurology visit — Thursday, 10:30 AM
            </h3>
            <p className="mt-1 text-sm text-[#6E7A74]">This is what walking in prepared looks like. Memory Lane builds it from what you&apos;ve already noticed — no night-before scramble.</p>

            <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-3">
                <div className={SECTION} data-testid="visitprep-questions">
                    <p className={LABEL}><CalendarCheck size={14} /> Questions to ask</p>
                    <ul className="mt-2 space-y-1.5 text-sm text-[#2A3330]">
                        <li>&bull; She&apos;s up 2–3 times a night now — is a sleep med safe with donepezil?</li>
                        <li>&bull; The evening agitation is new. Sundowning, or a medication issue?</li>
                        <li>&bull; Should we repeat the cognitive screen to track change since March?</li>
                        <li>&bull; Is she eligible for Medicare&apos;s GUIDE program through your practice?</li>
                    </ul>
                </div>
                <div className={SECTION} data-testid="visitprep-timeline">
                    <p className={LABEL}><History size={14} /> Behavior timeline (last 3 weeks)</p>
                    <ul className="mt-2 space-y-1.5 text-sm text-[#2A3330]">
                        <li><span className="font-medium">Jul 9</span> — Got lost driving back from church (10-min route)</li>
                        <li><span className="font-medium">Jul 17</span> — Accused aide of moving her purse; found it in freezer</li>
                        <li><span className="font-medium">Jul 24</span> — Awake 1–4 AM three nights running, pacing hallway</li>
                    </ul>
                </div>
                <div className={SECTION} data-testid="visitprep-meds">
                    <p className={LABEL}><Pill size={14} /> Medication reminders</p>
                    <ul className="mt-2 space-y-1.5 text-sm text-[#2A3330]">
                        <li>&bull; Donepezil 10mg — evenings (started March)</li>
                        <li>&bull; Lisinopril 20mg — mornings</li>
                        <li>&bull; Refill needed: donepezil runs out in 6 days</li>
                    </ul>
                </div>
                <div className={SECTION} data-testid="visitprep-summary">
                    <p className={LABEL}><FileText size={14} /> Care Summary preview</p>
                    <p className="mt-2 text-sm text-[#2A3330] leading-relaxed">
                        &ldquo;Margaret, 78, moderate-stage Alzheimer&apos;s (dx 11/2025). New this month: night waking,
                        evening agitation, one wandering episode. Primary caregiver: daughter Sarah (lives in).
                        Goals: safety at night, caregiver respite…&rdquo;
                    </p>
                    <p className="mt-1.5 text-xs text-[#9CA6A1]">Shared as one link — every provider reads the same story.</p>
                </div>
            </div>
            <TransitionPanel card="visit_prep" onClose={onClose} ctaLabel="Create an account to build yours before every appointment" />
        </div>
    );
}
